/**
 * Monaco Editor 主题工具函数
 *
 * 提供与TailwindCSS一致的主题定义和主题管理功能
 */

import * as monaco from 'monaco-editor'
import { MONACO_THEMES } from '@/constants/monaco-config'
import type { ThemeColors } from '@/types/monaco-editor'

/**
 * TailwindCSS 颜色系统映射
 */
const TAILWIND_COLORS = {
  // 灰色系
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
    950: '#030712',
  },
  // 蓝色系
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  // 绿色系
  green: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  // 紫色系
  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
  },
  // 粉色系
  pink: {
    50: '#fdf2f8',
    100: '#fce7f3',
    200: '#fbcfe8',
    300: '#f9a8d4',
    400: '#f472b6',
    500: '#ec4899',
    600: '#db2777',
    700: '#be185d',
    800: '#9d174d',
    900: '#831843',
  },
  // 琥珀色系
  amber: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  // 红色系
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
} as const

/**
 * 深色主题颜色配置
 */
const darkThemeColors: ThemeColors = {
  background: TAILWIND_COLORS.gray[900],
  foreground: TAILWIND_COLORS.gray[50],
  cursor: TAILWIND_COLORS.blue[400],
  selection: TAILWIND_COLORS.gray[700],
  lineNumber: TAILWIND_COLORS.gray[500],
  lineHighlightBackground: TAILWIND_COLORS.gray[800],
  selectionBackground: TAILWIND_COLORS.gray[700],
  activeLineBorder: TAILWIND_COLORS.gray[600],
  indentGuide: TAILWIND_COLORS.gray[700],
  whitespace: TAILWIND_COLORS.gray[600],
}

/**
 * 浅色主题颜色配置
 */
const lightThemeColors: ThemeColors = {
  background: '#ffffff',
  foreground: TAILWIND_COLORS.gray[900],
  cursor: TAILWIND_COLORS.blue[600],
  selection: TAILWIND_COLORS.gray[200],
  lineNumber: TAILWIND_COLORS.gray[400],
  lineHighlightBackground: TAILWIND_COLORS.gray[50],
  selectionBackground: TAILWIND_COLORS.gray[200],
  activeLineBorder: TAILWIND_COLORS.gray[300],
  indentGuide: TAILWIND_COLORS.gray[300],
  whitespace: TAILWIND_COLORS.gray[300],
}

/**
 * 定义与TailwindCSS一致的Monaco主题
 */
export const defineCustomThemes = (): void => {
  // 深色主题 - 匹配TailwindCSS dark模式
  monaco.editor.defineTheme('tailwind-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      // 注释
      { token: 'comment', foreground: TAILWIND_COLORS.gray[500] },
      { token: 'comment.doc', foreground: TAILWIND_COLORS.gray[400], fontStyle: 'italic' },

      // 关键字
      { token: 'keyword', foreground: TAILWIND_COLORS.pink[400] },
      { token: 'keyword.control', foreground: TAILWIND_COLORS.purple[400] },
      { token: 'keyword.operator', foreground: TAILWIND_COLORS.pink[400] },

      // 标识符
      { token: 'identifier', foreground: TAILWIND_COLORS.gray[50] },
      { token: 'identifier.function', foreground: TAILWIND_COLORS.blue[400] },
      { token: 'identifier.class', foreground: TAILWIND_COLORS.green[400] },
      { token: 'identifier.variable', foreground: TAILWIND_COLORS.amber[400] },

      // 字符串
      { token: 'string', foreground: TAILWIND_COLORS.green[300] },
      { token: 'string.escape', foreground: TAILWIND_COLORS.purple[400] },
      { token: 'string.regex', foreground: TAILWIND_COLORS.purple[400] },

      // 数字
      { token: 'number', foreground: TAILWIND_COLORS.amber[400] },
      { token: 'number.hex', foreground: TAILWIND_COLORS.purple[400] },

      // 类型
      { token: 'type', foreground: TAILWIND_COLORS.blue[400] },
      { token: 'type.builtin', foreground: TAILWIND_COLORS.purple[400] },

      // 函数
      { token: 'function', foreground: TAILWIND_COLORS.blue[400] },
      { token: 'function.builtin', foreground: TAILWIND_COLORS.purple[400] },

      // 变量
      { token: 'variable', foreground: TAILWIND_COLORS.gray[50] },
      { token: 'variable.parameter', foreground: TAILWIND_COLORS.amber[400] },

      // 常量
      { token: 'constant', foreground: TAILWIND_COLORS.purple[400] },
      { token: 'constant.builtin', foreground: TAILWIND_COLORS.pink[400] },

      // 标签
      { token: 'tag', foreground: TAILWIND_COLORS.pink[400] },
      { token: 'attribute.name', foreground: TAILWIND_COLORS.blue[400] },
      { token: 'attribute.value', foreground: TAILWIND_COLORS.green[300] },

      // 错误和警告
      { token: 'invalid', foreground: TAILWIND_COLORS.red[400], background: TAILWIND_COLORS.red[950] },
      { token: 'warning', foreground: TAILWIND_COLORS.amber[400] },

      // 元信息
      { token: 'meta', foreground: TAILWIND_COLORS.gray[400] },

      // 标点符号
      { token: 'delimiter', foreground: TAILWIND_COLORS.gray[400] },
      { token: 'delimiter.parenthesis', foreground: TAILWIND_COLORS.gray[300] },
      { token: 'delimiter.bracket', foreground: TAILWIND_COLORS.gray[300] },
      { token: 'delimiter.brace', foreground: TAILWIND_COLORS.gray[300] },

      // 其他
      { token: 'text', foreground: TAILWIND_COLORS.gray[50] },
    ],
    colors: {
      'editor.background': darkThemeColors.background,
      'editor.foreground': darkThemeColors.foreground,
      'editor.lineHighlightBackground': darkThemeColors.lineHighlightBackground,
      'editor.selectionBackground': darkThemeColors.selectionBackground,
      'editorCursor.foreground': darkThemeColors.cursor,
      'editorWhitespace.foreground': darkThemeColors.whitespace,
      'editorIndentGuide.background': darkThemeColors.indentGuide,
      'editorLineNumber.foreground': darkThemeColors.lineNumber,
      'editorLineNumber.activeForeground': TAILWIND_COLORS.gray[300],
      'editorActiveLine.background': darkThemeColors.lineHighlightBackground,
      'editorActiveLine.border': darkThemeColors.activeLineBorder,

      // 侧边栏
      'sideBar.background': TAILWIND_COLORS.gray[800],
      'sideBar.foreground': TAILWIND_COLORS.gray[300],
      'sideBarTitle.foreground': TAILWIND_COLORS.gray[100],

      // 面板
      'panel.background': TAILWIND_COLORS.gray[800],
      'panel.border': TAILWIND_COLORS.gray[700],
      'panelTitle.activeBorder': TAILWIND_COLORS.blue[400],

      // 标签页
      'tab.activeBackground': TAILWIND_COLORS.gray[900],
      'tab.activeForeground': TAILWIND_COLORS.gray[100],
      'tab.inactiveBackground': TAILWIND_COLORS.gray[800],
      'tab.inactiveForeground': TAILWIND_COLORS.gray[400],

      // 选择高亮
      'selection.background': TAILWIND_COLORS.blue[900],

      // 搜索高亮
      'editor.findMatchHighlightBackground': TAILWIND_COLORS.amber[900],
      'editor.findMatchBackground': TAILWIND_COLORS.amber[800],

      // 错误和警告
      'editorError.foreground': TAILWIND_COLORS.red[400],
      'editorWarning.foreground': TAILWIND_COLORS.amber[400],
      'editorInfo.foreground': TAILWIND_COLORS.blue[400],
      'editorHint.foreground': TAILWIND_COLORS.gray[400],
    },
  })

  // 浅色主题 - 匹配TailwindCSS light模式
  monaco.editor.defineTheme('tailwind-light', {
    base: 'vs',
    inherit: true,
    rules: [
      // 注释
      { token: 'comment', foreground: TAILWIND_COLORS.gray[400] },
      { token: 'comment.doc', foreground: TAILWIND_COLORS.gray[500], fontStyle: 'italic' },

      // 关键字
      { token: 'keyword', foreground: TAILWIND_COLORS.pink[600] },
      { token: 'keyword.control', foreground: TAILWIND_COLORS.purple[600] },
      { token: 'keyword.operator', foreground: TAILWIND_COLORS.pink[600] },

      // 标识符
      { token: 'identifier', foreground: TAILWIND_COLORS.gray[900] },
      { token: 'identifier.function', foreground: TAILWIND_COLORS.blue[600] },
      { token: 'identifier.class', foreground: TAILWIND_COLORS.green[600] },
      { token: 'identifier.variable', foreground: TAILWIND_COLORS.amber[600] },

      // 字符串
      { token: 'string', foreground: TAILWIND_COLORS.green[600] },
      { token: 'string.escape', foreground: TAILWIND_COLORS.purple[600] },
      { token: 'string.regex', foreground: TAILWIND_COLORS.purple[600] },

      // 数字
      { token: 'number', foreground: TAILWIND_COLORS.amber[600] },
      { token: 'number.hex', foreground: TAILWIND_COLORS.purple[600] },

      // 类型
      { token: 'type', foreground: TAILWIND_COLORS.blue[600] },
      { token: 'type.builtin', foreground: TAILWIND_COLORS.purple[600] },

      // 函数
      { token: 'function', foreground: TAILWIND_COLORS.blue[600] },
      { token: 'function.builtin', foreground: TAILWIND_COLORS.purple[600] },

      // 变量
      { token: 'variable', foreground: TAILWIND_COLORS.gray[900] },
      { token: 'variable.parameter', foreground: TAILWIND_COLORS.amber[600] },

      // 常量
      { token: 'constant', foreground: TAILWIND_COLORS.purple[600] },
      { token: 'constant.builtin', foreground: TAILWIND_COLORS.pink[600] },

      // 标签
      { token: 'tag', foreground: TAILWIND_COLORS.pink[600] },
      { token: 'attribute.name', foreground: TAILWIND_COLORS.blue[600] },
      { token: 'attribute.value', foreground: TAILWIND_COLORS.green[600] },

      // 错误和警告
      { token: 'invalid', foreground: TAILWIND_COLORS.red[600], background: TAILWIND_COLORS.red[50] },
      { token: 'warning', foreground: TAILWIND_COLORS.amber[600] },

      // 元信息
      { token: 'meta', foreground: TAILWIND_COLORS.gray[500] },

      // 标点符号
      { token: 'delimiter', foreground: TAILWIND_COLORS.gray[500] },
      { token: 'delimiter.parenthesis', foreground: TAILWIND_COLORS.gray[600] },
      { token: 'delimiter.bracket', foreground: TAILWIND_COLORS.gray[600] },
      { token: 'delimiter.brace', foreground: TAILWIND_COLORS.gray[600] },

      // 其他
      { token: 'text', foreground: TAILWIND_COLORS.gray[900] },
    ],
    colors: {
      'editor.background': lightThemeColors.background,
      'editor.foreground': lightThemeColors.foreground,
      'editor.lineHighlightBackground': lightThemeColors.lineHighlightBackground,
      'editor.selectionBackground': lightThemeColors.selectionBackground,
      'editorCursor.foreground': lightThemeColors.cursor,
      'editorWhitespace.foreground': lightThemeColors.whitespace,
      'editorIndentGuide.background': lightThemeColors.indentGuide,
      'editorLineNumber.foreground': lightThemeColors.lineNumber,
      'editorLineNumber.activeForeground': TAILWIND_COLORS.gray[600],
      'editorActiveLine.background': lightThemeColors.lineHighlightBackground,
      'editorActiveLine.border': lightThemeColors.activeLineBorder,

      // 侧边栏
      'sideBar.background': TAILWIND_COLORS.gray[100],
      'sideBar.foreground': TAILWIND_COLORS.gray[700],
      'sideBarTitle.foreground': TAILWIND_COLORS.gray[900],

      // 面板
      'panel.background': TAILWIND_COLORS.gray[50],
      'panel.border': TAILWIND_COLORS.gray[200],
      'panelTitle.activeBorder': TAILWIND_COLORS.blue[500],

      // 标签页
      'tab.activeBackground': '#ffffff',
      'tab.activeForeground': TAILWIND_COLORS.gray[900],
      'tab.inactiveBackground': TAILWIND_COLORS.gray[100],
      'tab.inactiveForeground': TAILWIND_COLORS.gray[600],

      // 选择高亮
      'selection.background': TAILWIND_COLORS.blue[100],

      // 搜索高亮
      'editor.findMatchHighlightBackground': TAILWIND_COLORS.amber[100],
      'editor.findMatchBackground': TAILWIND_COLORS.amber[200],

      // 错误和警告
      'editorError.foreground': TAILWIND_COLORS.red[500],
      'editorWarning.foreground': TAILWIND_COLORS.amber[500],
      'editorInfo.foreground': TAILWIND_COLORS.blue[500],
      'editorHint.foreground': TAILWIND_COLORS.gray[500],
    },
  })
}

/**
 * 获取系统主题偏好
 */
export const getSystemThemePreference = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'dark'
}

/**
 * 获取对应的Monaco主题名称
 */
export const getMonacoThemeName = (preference: 'light' | 'dark' | 'system'): string => {
  if (preference === 'system') {
    preference = getSystemThemePreference()
  }
  return preference === 'dark' ? MONACO_THEMES.dark : MONACO_THEMES.light
}

/**
 * 应用主题到Monaco Editor
 */
export const applyTheme = (theme: string): void => {
  try {
    monaco.editor.setTheme(theme)
    console.debug(`Applied Monaco theme: ${theme}`)
  } catch (error) {
    console.error(`Failed to apply theme ${theme}:`, error)
    // 回退到默认主题
    monaco.editor.setTheme(MONACO_THEMES['vs-dark'])
  }
}

/**
 * 获取所有可用主题
 */
export const getAvailableThemes = (): Array<{ name: string; label: string; description: string }> => {
  return [
    { name: MONACO_THEMES.light, label: 'Tailwind Light', description: 'Light theme matching TailwindCSS' },
    { name: MONACO_THEMES.dark, label: 'Tailwind Dark', description: 'Dark theme matching TailwindCSS' },
    { name: MONACO_THEMES.vs, label: 'VS Light', description: 'Visual Studio Light theme' },
    { name: MONACO_THEMES['vs-dark'], label: 'VS Dark', description: 'Visual Studio Dark theme' },
    { name: MONACO_THEMES['hc-black'], label: 'High Contrast', description: 'High Contrast theme' },
  ]
}

/**
 * 初始化主题系统
 */
export const initializeThemes = (): void => {
  // 定义自定义主题
  defineCustomThemes()

  // 设置初始主题
  const systemPreference = getSystemThemePreference()
  const initialTheme = getMonacoThemeName(systemPreference)
  applyTheme(initialTheme)
}
